import type { Lead } from '@/types';
import type { ScoringResult } from '@/types/scoring';
import type { ProductConfig } from '@/types/product';

const RESEND_API_URL = 'https://api.resend.com/emails';
const RESEND_API_KEY = import.meta.env.VITE_RESEND_API_KEY as string | undefined;
const FROM = 'notify@mail.zbdigital.dev';
const TO = 'contato@inovafranquias.com.br';
const CC = 'comercial@inovafranquias.com.br';
const EMAIL_API_URL = import.meta.env.VITE_EMAIL_API_URL as string | undefined;

function ensureTrailingSlash(url: string): string {
  if (url.endsWith('/')) return url;
  return url + '/';
}

async function blobToBase64Content(blob: Blob): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('file_reader_error'));
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsDataURL(blob);
  });
  const pos = dataUrl.indexOf('base64,');
  if (pos === -1) return '';
  return dataUrl.slice(pos + 7);
}

const PRODUCT_LABELS: Record<string, string> = {
  franqueabilidade: 'Diagnóstico de Franqueabilidade',
  select: 'Diagnóstico Select (Franqueado)',
  expansao: 'Diagnóstico de Expansão de Rede'
};

const STATUS_LABELS: Record<string, string> = {
  ok: 'Bom',
  attention: 'Atenção',
  critical: 'Crítico'
};

function buildHtml(lead: Lead, scoring: ScoringResult, product: ProductConfig): string {
  const productLabel = PRODUCT_LABELS[product.id] ?? product.title;
  const percentage = Math.round(scoring.totalPercentage);
  const scoreColor = percentage >= 70 ? '#16a34a' : percentage >= 50 ? '#d97706' : '#dc2626';

  const categoryRows = scoring.categoryScores
    .map(c => `
      <tr>
        <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;">${c.label}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;text-align:center;">${c.earnedPoints}/${c.maxPoints}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;text-align:center;">${Math.round(c.percentage)}%</td>
        <td style="padding:6px 8px;border-bottom:1px solid #f1f5f9;text-align:center;">${STATUS_LABELS[c.status] ?? c.status}</td>
      </tr>`)
    .join('');

  return `<!DOCTYPE html>
<html lang="pt-BR">
<head><meta charset="UTF-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>
<body style="margin:0;padding:0;background:#f8fafc;font-family:Arial,sans-serif;color:#1e293b;">
  <div style="max-width:640px;margin:32px auto;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.08);">

    <!-- Header -->
    <div style="background:#1e293b;padding:24px 32px;">
      <p style="margin:0;color:#94a3b8;font-size:13px;text-transform:uppercase;letter-spacing:1px;">Inova Franquias</p>
      <h1 style="margin:4px 0 0;color:#fff;font-size:20px;">${productLabel}</h1>
    </div>

    <!-- Score badge -->
    <div style="background:#f1f5f9;padding:20px 32px;display:flex;align-items:center;gap:16px;">
      <div style="background:#fff;border-radius:50%;width:72px;height:72px;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(0,0,0,.1);">
        <span style="font-size:22px;font-weight:700;color:${scoreColor};">${percentage}%</span>
      </div>
      <div>
        <p style="margin:0;font-size:13px;color:#64748b;">Resultado final</p>
        <p style="margin:2px 0 0;font-size:17px;font-weight:700;color:#1e293b;">${scoring.finalRecommendation}</p>
      </div>
    </div>

    <div style="padding:24px 32px;">

      <!-- Lead info -->
      <h2 style="margin:0 0 12px;font-size:15px;color:#475569;text-transform:uppercase;letter-spacing:.5px;">Dados do Lead</h2>
      <table style="width:100%;border-collapse:collapse;margin-bottom:24px;">
        <tr><td style="padding:5px 0;color:#64748b;width:110px;">Nome</td><td style="padding:5px 0;font-weight:600;">${lead.nome}</td></tr>
        <tr><td style="padding:5px 0;color:#64748b;">E-mail</td><td style="padding:5px 0;">${lead.email ?? '—'}</td></tr>
        <tr><td style="padding:5px 0;color:#64748b;">Telefone</td><td style="padding:5px 0;">${lead.telefone}</td></tr>
        <tr><td style="padding:5px 0;color:#64748b;">Cidade</td><td style="padding:5px 0;">${lead.cidade ?? '—'}</td></tr>
        <tr><td style="padding:5px 0;color:#64748b;">Lead ID</td><td style="padding:5px 0;">${lead.id || '—'}</td></tr>
      </table>

      <!-- Category scores -->
      <h2 style="margin:0 0 12px;font-size:15px;color:#475569;text-transform:uppercase;letter-spacing:.5px;">Pontuação por Categoria</h2>
      <table style="width:100%;border-collapse:collapse;font-size:14px;margin-bottom:24px;">
        <thead>
          <tr style="background:#f8fafc;">
            <th style="padding:8px;text-align:left;color:#64748b;font-weight:600;">Categoria</th>
            <th style="padding:8px;text-align:center;color:#64748b;font-weight:600;">Pontos</th>
            <th style="padding:8px;text-align:center;color:#64748b;font-weight:600;">%</th>
            <th style="padding:8px;text-align:center;color:#64748b;font-weight:600;">Status</th>
          </tr>
        </thead>
        <tbody>${categoryRows}</tbody>
      </table>

      <!-- Total -->
      <div style="background:#f8fafc;border-radius:8px;padding:14px 16px;display:flex;justify-content:space-between;align-items:center;">
        <span style="font-weight:700;font-size:15px;">Pontuação Total</span>
        <span style="font-weight:700;font-size:15px;color:${scoreColor};">${scoring.totalScore} pts &nbsp;·&nbsp; ${percentage}%</span>
      </div>

    </div>

    <!-- Footer -->
    <div style="border-top:1px solid #f1f5f9;padding:16px 32px;text-align:center;">
      <p style="margin:0;font-size:12px;color:#94a3b8;">Enviado automaticamente pelo sistema de diagnóstico — Inova Franquias</p>
    </div>

  </div>
</body>
</html>`;
}

export async function sendDiagnosticEmail(
  lead: Lead,
  scoring: ScoringResult,
  product: ProductConfig,
  pdfBlob?: Blob
): Promise<void> {
  const productLabel = PRODUCT_LABELS[product.id] ?? product.title;
  const percentage = Math.round(scoring.totalPercentage);

  const subject = `[Diagnóstico] ${productLabel} — ${lead.nome} (${percentage}%)`;
  const html = buildHtml(lead, scoring, product);
  const pdfContent = pdfBlob ? await blobToBase64Content(pdfBlob) : '';
  const attachments = pdfContent
    ? [{ filename: `diagnostico_${lead.sessionId || lead.id || 'lead'}.pdf`, content: pdfContent }]
    : [];

  if (EMAIL_API_URL) {
    const apiUrl = ensureTrailingSlash(EMAIL_API_URL);
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        leadId: lead.id || null,
        sessionId: lead.sessionId || null,
        subject,
        html,
        honeypot: lead.honeypot || '',
        attachments
      })
    });

    if (response.status === 204) {
      throw new Error('Email suppressed by backend');
    }
    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Email API error: ${response.status} — ${text}`);
    }
  } else {
    if (!RESEND_API_KEY) {
      throw new Error('Missing VITE_RESEND_API_KEY');
    }
    const payload = {
      from: FROM,
      to: [TO],
      cc: [CC],
      subject,
      html,
      attachments: attachments.length ? attachments : undefined
    };

    const response = await fetch(RESEND_API_URL, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${RESEND_API_KEY}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Resend API error: ${response.status} — ${text}`);
    }
  }

  console.log('[Email] Diagnostic email sent successfully');
}
